import { colors } from "@permisso/permisso-components";
import { useEffect, useState } from "react";
import { Button } from "src/apps/components/button/Button";
import { Icon, ICON_SIZES } from "src/apps/components/icon/Icon";
import { ICON_PATHS } from "src/apps/components/icon/IconSVGPaths";
import { Text, TextBold } from "src/apps/components/typography/Texts";

import { MEDIA_MOBILE_BREAKPOINT } from "src/apps/config/constants";
import { useIdentityVerification } from "src/apps/hooks/identityVerification/useIdentityVerification";
import { useCreateOpenAdminIDVSession } from "src/apps/hooks/useCreateOpenAdminIDVSession";
import { useIsMedia } from "src/apps/hooks/useIsMedia";
import { useTranslations } from "src/apps/hooks/useTranslations";
import { OpenAdminWidgetEventNames } from "src/contexts/documentationArea/application/OpenAdminWidgetEventNames";
import { OrderStatus } from "src/contexts/documentationArea/domain/OrderStatus";
import styled, { useTheme } from "styled-components";

const BANNER_TITLE = "documentation_area.identity_verification.open_admin_banner.title";
const BANNER_SUBTITLE_MOBILE =
  "documentation_area.identity_verification.open_admin_banner.subtitle.mobile";
const BANNER_SUBTITLE_DESKTOP =
  "documentation_area.identity_verification.open_admin_banner.subtitle.desktop";
const BANNER_BUTTON_TITLE = "documentation_area.identity_verification.open_admin_banner.button";

interface IdentityVerificationsOpenAdminSectionProps {
  onClick: () => void;
  isSelected: boolean;
  isReadonly: boolean;
}

export const IdentityVerificationsOpenAdminSection = ({
  onClick,
  isSelected,
  isReadonly,
}: IdentityVerificationsOpenAdminSectionProps) => {
  const { isMobile } = useIsMedia();
  const { primaryColor, blackColor } = useTheme();
  const { isPending, createOpenAdminIDVSession, widgetLink } = useCreateOpenAdminIDVSession();
  const { identityVerificationStatus } = useIdentityVerification();
  const [newTab, setNewTab] = useState<Window | null>(null);
  const { t } = useTranslations();

  const onButtonClick = async () => {
    onClick();
    const windowName = "documentationAreaIDVWindow";
    const openedTab = window.open("", windowName);

    setNewTab(openedTab);

    const interval = setInterval(() => {
      if (openedTab?.closed) {
        clearInterval(interval);
      }
    }, 500);
    createOpenAdminIDVSession();
  };

  useEffect(() => {
    if (!newTab || !widgetLink) {
      return;
    }

    newTab.location.href = widgetLink;

    const messageHandler = (event: MessageEvent) => {
      if (event.data?.name === OpenAdminWidgetEventNames.END_FLOW_BASE_SCREEN) {
        newTab?.close();
      }
    };

    window.addEventListener("message", messageHandler);

    return () => {
      window.removeEventListener("message", messageHandler);
    };
  }, [widgetLink, newTab]);

  return (
    <Card isMobile={isMobile} $isSelected={isSelected}>
      <Container>
        <Shape>
          <Icon path={ICON_PATHS.SCAN} size={ICON_SIZES.s24} color={primaryColor} />
        </Shape>
        <TextContainer>
          <TextBold $color={blackColor}>{t(BANNER_TITLE)}</TextBold>
          <Text $color={colors.grey700}>
            {isMobile ? t(BANNER_SUBTITLE_MOBILE) : t(BANNER_SUBTITLE_DESKTOP)}
          </Text>
        </TextContainer>
      </Container>
      {!isReadonly ? (
        <ButtonContainer>
          <Button
            isLoading={isPending}
            leftIcon={
              isMobile ? (
                <Icon path={ICON_PATHS.LOCK} color={"currentColor"} size={ICON_SIZES.s14} />
              ) : undefined
            }
            onClick={onButtonClick}
            text={t(BANNER_BUTTON_TITLE)}
            disabled={identityVerificationStatus === OrderStatus.PROCESSING}
          />
        </ButtonContainer>
      ) : null}
    </Card>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  gap: 1.5rem;
  @media (max-width: ${MEDIA_MOBILE_BREAKPOINT}rem) {
    display: flex;
    flex: 1;
    flex-direction: row;
  }
`;

const Card = styled.div<{ isMobile?: boolean; $isSelected: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1.5rem;
  padding: 2rem;
  border-radius: ${({ isMobile }) => (isMobile ? "0.5rem" : "0.938rem")};
  border: 0.0625rem solid
    ${({ theme, $isSelected }) => ($isSelected ? theme.primaryColor : colors.grey400)};
  min-height: 6.875rem;
  @media (max-width: ${MEDIA_MOBILE_BREAKPOINT}rem) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    min-height: unset;
    padding: 0.75rem;
  }
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: center;
  gap: 0.5rem;
`;

const Shape = styled.div`
  display: flex;
  width: 3.125rem;
  height: 3.125rem;
  align-items: center;
  justify-content: center;
  background-color: ${colors.grey0};
  border-radius: 0.5rem;
  filter: drop-shadow(0.927px 0.927px 4.644px rgba(0, 0, 0, 0.08));
`;

const ButtonContainer = styled.div`
  display: flex;
  width: fit-content;
  @media (max-width: ${MEDIA_MOBILE_BREAKPOINT}rem) {
    width: 100%;
  }
`;
